// Global packages and components
import { Accordion, SafetyIcon } from '~/components'

// Types
import type { ProductSpecification, SafetyIconNames } from '~/types'

interface Props {
  parentData: {
    description: string
  }
  variantData: {
    title?: string
    stockCode?: string
    specifications?: ProductSpecification[]
    safetyIcons?: SafetyIconNames[]
    bulletPoints?: string[]
    videos?: string
  }
}

/*
  Product detail accordions. Schema lives in the route's JSON-LD, not here, so
  there is no microdata to keep in step.
*/
const ProductDetails = ({ parentData, variantData }: Props) => {
  const hasSpecifications = (variantData?.specifications?.length ?? 0) > 0
  const hasSafetyIcons = (variantData?.safetyIcons?.length ?? 0) > 0
  const hasBulletPoints = (variantData?.bulletPoints?.length ?? 0) > 0

  return (
    <Accordion
      sectionStyle="minimal"
      titleSize="md"
      titleWeight="bold"
    >
      <Accordion.Section
        title="Product description"
        className="mb-2"
      >
        {(variantData?.title || variantData?.stockCode) && (
          <div className="mb-5">
            {variantData?.title && (
              <p className="mb-1 text-sm">
                <span className="font-semibold">Product name:</span>
                &nbsp;&nbsp;{variantData.title}
              </p>
            )}
            {variantData?.stockCode && (
              <p className="mb-1 text-sm">
                <span className="font-semibold">Product code:</span>&nbsp;&nbsp;
                {variantData.stockCode}
              </p>
            )}
          </div>
        )}
        {parentData?.description && (
          <div
            dangerouslySetInnerHTML={{ __html: parentData.description }}
            className="mb-5"
          />
        )}
        {hasBulletPoints && (
          <ul className="list-disc pl-5">
            {variantData.bulletPoints?.map((text, i) => (
              <li key={i}>{text}</li>
            ))}
          </ul>
        )}
      </Accordion.Section>
      {hasSpecifications && (
        <Accordion.Section
          title="Specifications"
          className="mb-2"
        >
          <ul className="list-disc pl-5">
            {variantData.specifications?.map((e, i) => (
              <li key={i}>
                <span className="font-semibold">{e.label}:</span>&nbsp;
                {`${e.value ?? ''}${e?.unit ?? ''}`}
              </li>
            ))}
          </ul>
        </Accordion.Section>
      )}
      {hasSafetyIcons && (
        <Accordion.Section
          title="Safety information"
          className="mb-2"
        >
          <ul>
            {variantData.safetyIcons?.map((e, i) => (
              <li
                className="mb-1 flex items-center"
                key={i}
              >
                <SafetyIcon icon={e} />
              </li>
            ))}
          </ul>
        </Accordion.Section>
      )}
    </Accordion>
  )
}

export default ProductDetails
